import { Fragment, ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Card from '../cards/Card';
import { IconAngleDown, IconAngleUp } from '@icons';

export interface SelectOption {
    value: string;
    label: string;
    content?: ReactNode;
    /** Non-selectable rule above this option. Skipped by the keyboard. */
    dividerBefore?: boolean;
}

export interface SelectFooterAction {
    label: string;
    onClick: () => void;
}

/** Same add row Equipment uses: orange “+ Add …” text, not a separate button. */
export interface SelectActionOption {
    label: string;
    onClick: () => void;
}

const SELECT_MENU_MAX_HEIGHT = 280;

/** Bottom edge of the nearest ancestor that clips overflow, so the menu can scroll inside it. */
const clippingBottom = (element: HTMLElement) => {
    let bottom = window.innerHeight;
    let parent = element.parentElement;
    while (parent && parent !== document.documentElement) {
        const overflowY = getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden' || overflowY === 'clip') {
            bottom = Math.min(bottom, parent.getBoundingClientRect().bottom);
        }
        parent = parent.parentElement;
    }
    return bottom;
};

export interface SelectProps {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    hasError?: boolean;
    emptyMessage?: string;
    /** Orange “+ Add …” row at the bottom of the list. Same treatment as Equipment. */
    actionOption?: SelectActionOption;
    /** @deprecated Use `actionOption`. Still rendered as that same row. */
    footerAction?: SelectFooterAction;
    className?: string;
    ariaLabel?: string;
    /** Optional id for the trigger button so an external `<label htmlFor>` can target it. */
    inputId?: string;
    onBlur?: () => void;
    onTriggerKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
    ariaDescribedBy?: string;
    dropdownCardVariant?: 'card-sm' | 'card-lg';
    wrapDropdownInCard?: boolean;
    renderOption?: (option: SelectOption, isSelected: boolean) => ReactNode;
}

export const Select = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    disabled = false,
    hasError = false,
    emptyMessage = 'No options available',
    actionOption,
    footerAction,
    className,
    ariaLabel,
    inputId,
    onBlur,
    onTriggerKeyDown,
    ariaDescribedBy,
    dropdownCardVariant = 'card-sm',
    wrapDropdownInCard = true,
    renderOption,
}: SelectProps) => {
    const generatedId = useId();
    const triggerId = inputId || `${generatedId}-trigger`;
    const listboxId = `${generatedId}-listbox`;
    const [isOpen, setIsOpen] = useState(false);
    const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
    const rootRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value),
        [options, value],
    );

    const selectedLabel = selectedOption?.label || '';
    const selectedIndex = useMemo(() => options.findIndex((option) => option.value === value), [options, value]);
    const addAction = actionOption ?? footerAction;
    const addIndex = addAction ? options.length : -1;
    const listLength = options.length + (addAction ? 1 : 0);

    const closeDropdown = () => {
        setIsOpen(false);
        setActiveOptionIndex(-1);
    };

    const getInitialActiveIndex = () => {
        if (listLength === 0) return -1;
        return selectedIndex >= 0 ? selectedIndex : 0;
    };

    const activateAddAction = () => {
        addAction?.onClick();
        closeDropdown();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (rootRef.current?.contains(event.target as Node)) return;
            closeDropdown();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setActiveOptionIndex(-1);
        }
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return;
        const menu = rootRef.current?.querySelector('.select-component-dropdown');
        if (!(menu instanceof HTMLElement)) return;

        const fitToViewport = () => {
            const top = menu.getBoundingClientRect().top;
            const available = Math.max(120, Math.floor(clippingBottom(menu) - top - 8));
            menu.style.maxHeight = `${Math.min(SELECT_MENU_MAX_HEIGHT, available)}px`;
        };

        fitToViewport();
        window.addEventListener('resize', fitToViewport);
        return () => {
            menu.style.maxHeight = '';
            window.removeEventListener('resize', fitToViewport);
        };
    }, [isOpen, options.length]);

    useEffect(() => {
        if (activeOptionIndex < 0 || !dropdownRef.current) return;
        const activeEl = dropdownRef.current.querySelector(`[data-option-index="${activeOptionIndex}"]`);
        activeEl?.scrollIntoView({ block: 'nearest' });
    }, [activeOptionIndex]);

    const rootClassName = [
        'select-component',
        isOpen ? 'is-open' : '',
        disabled ? 'is-disabled' : '',
        hasError ? 'has-error' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        closeDropdown();
    };

    const renderOptionContent = (option: SelectOption, isSelected: boolean) => {
        if (renderOption) {
            return renderOption(option, isSelected);
        }

        return option.content ?? <span className="select-component-option-label">{option.label}</span>;
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        onTriggerKeyDown?.(event);
        if (disabled) return;

        if (event.key === 'Escape') {
            closeDropdown();
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (listLength === 0) return;

            setIsOpen(true);
            setActiveOptionIndex((current) => {
                if (!isOpen || current < 0) return getInitialActiveIndex();
                return (current + 1) % listLength;
            });
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (listLength === 0) return;

            setIsOpen(true);
            setActiveOptionIndex((current) => {
                if (!isOpen || current < 0) return listLength - 1;
                return current <= 0 ? listLength - 1 : current - 1;
            });
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();

            if (!isOpen) {
                setIsOpen(true);
                setActiveOptionIndex(getInitialActiveIndex());
                return;
            }

            if (addAction && activeOptionIndex === addIndex) {
                activateAddAction();
                return;
            }

            if (activeOptionIndex >= 0 && options[activeOptionIndex]) {
                handleSelect(options[activeOptionIndex].value);
            }
        }
    };

    const dropdownBody = (
        <>
            {options.length === 0 && <div className="select-component-empty">{emptyMessage}</div>}
            {options.map((option, index) => {
                const isSelected = value === option.value;
                const optionButton = (
                    <button
                        id={`${generatedId}-option-${index}`}
                        data-option-index={index}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={[
                            'select-component-option',
                            isSelected ? 'is-selected' : '',
                            index === activeOptionIndex ? 'is-active' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActiveOptionIndex(index)}
                        onClick={() => handleSelect(option.value)}
                    >
                        {renderOptionContent(option, isSelected)}
                    </button>
                );

                return (
                    <Fragment key={option.value}>
                        {option.dividerBefore ? (
                            <div className="select-component-separator" aria-hidden="true" />
                        ) : null}
                        {optionButton}
                    </Fragment>
                );
            })}
            {addAction && (
                <button
                    id={`${generatedId}-option-add`}
                    data-option-index={addIndex}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className={[
                        'select-component-option',
                        'select-component-option--new',
                        activeOptionIndex === addIndex ? 'is-active' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveOptionIndex(addIndex)}
                    onClick={activateAddAction}
                >
                    <span className="select-component-option-label">{addAction.label}</span>
                </button>
            )}
        </>
    );

    return (
        <div ref={rootRef} className={rootClassName}>
            <button
                id={triggerId}
                type="button"
                className="select-component-trigger"
                disabled={disabled}
                role="combobox"
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={
                    isOpen && activeOptionIndex === addIndex
                        ? `${generatedId}-option-add`
                        : isOpen && activeOptionIndex >= 0
                          ? `${generatedId}-option-${activeOptionIndex}`
                          : undefined
                }
                onClick={() => {
                    if (disabled) return;
                    setIsOpen((open) => {
                        if (open) {
                            setActiveOptionIndex(-1);
                            return false;
                        }
                        setActiveOptionIndex(getInitialActiveIndex());
                        return true;
                    });
                }}
                onBlur={onBlur}
                onKeyDown={handleTriggerKeyDown}
            >
                <span className={selectedLabel ? 'select-component-value' : 'select-component-placeholder'}>
                    {selectedLabel || placeholder}
                </span>
                <span className="select-component-caret-wrap" aria-hidden="true">
                    {isOpen ? (
                        <IconAngleUp iconClasses="select-component-caret" />
                    ) : (
                        <IconAngleDown iconClasses="select-component-caret" />
                    )}
                </span>
            </button>

            {isOpen && !disabled &&
                (wrapDropdownInCard ? (
                    <Card
                        id={listboxId}
                        variant={dropdownCardVariant}
                        className="select-component-dropdown"
                        role="listbox"
                    >
                        <div ref={dropdownRef}>{dropdownBody}</div>
                    </Card>
                ) : (
                    <div ref={dropdownRef} id={listboxId} className="select-component-dropdown" role="listbox">
                        {dropdownBody}
                    </div>
                ))}
        </div>
    );
};
